// StoryCarousel — Instagram-style horizontal ring of unviewed/viewed stories.
// Renders at the top of TraineeDashboard. Tapping opens StoryViewer.
import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '@/lib/api';
import { useCourseLang } from '@/lib/courseLang';
import { X, ChevronLeft, ChevronRight, ExternalLink, Volume2, VolumeX } from 'lucide-react';

const STORY_DURATION_MS = 10000;      // image story — min 10s before auto-advancing
const EMBED_DURATION_MS = 15000;      // youtube/embed story

export default function StoryCarousel() {
    const [stories, setStories] = useState([]);
    const [openIdx, setOpenIdx] = useState(-1);
    const [lang] = useCourseLang();
    const token = typeof window !== 'undefined' ? localStorage.getItem('utc_token') : null;

    const load = useCallback(async () => {
        try {
            const r = await api.get(`/stories/feed?language=${encodeURIComponent(lang || 'en')}`);
            setStories(r.data || []);
        } catch { /* silent */ }
    }, [lang]);

    useEffect(() => { load(); }, [load]);

    const mediaUrl = useCallback((s) => s.is_external ? s.media_url : `${process.env.REACT_APP_BACKEND_URL}${s.media_url}?auth=${encodeURIComponent(token || '')}`, [token]);
    const thumbUrl = useCallback((s) => !s.thumbnail_url ? null : (s.thumbnail_is_external ? s.thumbnail_url : `${process.env.REACT_APP_BACKEND_URL}${s.thumbnail_url}?auth=${encodeURIComponent(token || '')}`), [token]);

    if (stories.length === 0) return null;

    return (
        <div data-testid="story-carousel" className="-mx-1">
            <div className="flex gap-3 overflow-x-auto px-1 py-2 no-scrollbar">
                {stories.map((s, i) => (
                    <button
                        key={s.id}
                        onClick={() => setOpenIdx(i)}
                        data-testid={`story-thumb-${s.id}`}
                        className="flex-shrink-0 flex flex-col items-center gap-1.5 group">
                        <span
                            className={`relative w-[76px] h-[112px] sm:w-[88px] sm:h-[128px] rounded-2xl p-[3px] ${s.viewed
                                ? 'bg-slate-300'
                                : 'bg-gradient-to-tr from-[#00DB79] via-emerald-500 to-teal-500'}`}>
                            <span className="block w-full h-full rounded-[14px] p-[2px] bg-white overflow-hidden">
                                {thumbUrl(s)
                                    ? <img src={thumbUrl(s)} alt="" className="w-full h-full object-cover rounded-[12px]" loading="lazy" />
                                    : <span className="w-full h-full grid place-items-center rounded-[12px] bg-gradient-to-br from-[#00DB79] to-emerald-600 text-white font-black text-2xl">{(s.caption || 'S')[0]}</span>}
                            </span>
                        </span>
                        <span className="text-[12px] sm:text-xs font-semibold max-w-[88px] truncate" style={{ color: '#E5E5E5' }}>
                            {(s.caption || 'Story').slice(0, 14)}
                        </span>
                    </button>
                ))}
            </div>

            <AnimatePresence>
                {openIdx >= 0 && (
                    <StoryViewer
                        stories={stories}
                        startIdx={openIdx}
                        mediaUrl={mediaUrl}
                        onClose={() => { setOpenIdx(-1); load(); }}
                        onViewed={(id) => setStories((prev) => prev.map((p) => p.id === id ? { ...p, viewed: true } : p))}
                    />
                )}
            </AnimatePresence>
        </div>
    );
}

function StoryViewer({ stories, startIdx, onClose, onViewed, mediaUrl }) {
    const [idx, setIdx] = useState(startIdx);
    const [progress, setProgress] = useState(0);
    const [paused, setPaused] = useState(false);
    const [muted, setMuted] = useState(false);   // stories open on a user tap, so start WITH sound (doc Point 34)
    const rafRef = useRef(null);
    const startRef = useRef(0);
    const elapsedRef = useRef(0);
    const reportedRef = useRef(new Set());
    const videoRef = useRef(null);
    const current = stories[idx];

    const advance = useCallback((dir = 1) => {
        const next = idx + dir;
        if (next < 0) { setIdx(0); setProgress(0); elapsedRef.current = 0; return; }
        if (next >= stories.length) { onClose(); return; }
        setIdx(next);
        setProgress(0);
        elapsedRef.current = 0;
    }, [idx, stories.length, onClose]);

    // Report view to backend (once per story per viewer)
    useEffect(() => {
        if (!current) return;
        if (reportedRef.current.has(current.id)) return;
        reportedRef.current.add(current.id);
        api.post(`/stories/${current.id}/view`).catch(() => { /* silent */ });
        onViewed(current.id);
    }, [current, onViewed]);

    // Auto-advance progress loop (images + embeds). Videos drive their own progress
    // via onTimeUpdate and advance on `ended`, so they always play fully to the end.
    useEffect(() => {
        if (!current) return;
        if (current.media_kind === 'video') return;
        const duration = current.media_kind === 'embed' ? EMBED_DURATION_MS : STORY_DURATION_MS;
        startRef.current = performance.now();
        const tick = (now) => {
            if (paused) {
                startRef.current = now - elapsedRef.current;
                rafRef.current = requestAnimationFrame(tick);
                return;
            }
            elapsedRef.current = now - startRef.current;
            const pct = Math.min(100, (elapsedRef.current / duration) * 100);
            setProgress(pct);
            if (pct >= 100) {
                advance(1);
                return;
            }
            rafRef.current = requestAnimationFrame(tick);
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, [idx, paused, current, advance]);

    // Pause/resume the playing video while the user holds down; keep sound state in sync.
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        v.muted = muted;
        if (paused) { v.pause(); return; }
        v.play().catch(() => {
            // Browser blocked autoplay WITH sound — fall back to muted so the story still plays.
            if (!v.muted) { v.muted = true; setMuted(true); v.play().catch(() => { /* ignore */ }); }
        });
    }, [paused, idx, muted]);

    // Keyboard
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowRight') advance(1);
            else if (e.key === 'ArrowLeft') advance(-1);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [advance, onClose]);

    if (!current) return null;

    return createPortal((
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-md grid place-items-center p-0 sm:p-4"
            data-testid="story-viewer">
            <div className="relative w-full h-full sm:h-auto sm:max-w-sm sm:aspect-[9/16] rounded-none sm:rounded-2xl overflow-hidden bg-slate-900 shadow-2xl select-none">
                {/* Progress bars (kept clear of the status bar via safe-area inset) */}
                <div className="absolute top-0 left-0 right-0 z-30 flex gap-1 px-3 pt-[max(0.6rem,env(safe-area-inset-top))]">
                    {stories.map((s, i) => (
                        <div key={s.id} className="flex-1 h-1 rounded-full bg-white/25 overflow-hidden">
                            <div
                                className="h-full bg-white"
                                style={{ width: i < idx ? '100%' : i === idx ? `${progress}%` : '0%' }} />
                        </div>
                    ))}
                </div>
                {/* Close */}
                <button
                    onClick={onClose} data-testid="story-close-btn"
                    className="absolute top-[max(1.1rem,calc(env(safe-area-inset-top)+0.4rem))] right-3 z-30 w-8 h-8 grid place-items-center rounded-full bg-black/40 text-white hover:bg-black/70">
                    <X className="w-4 h-4" />
                </button>
                {/* Sound toggle (video stories only) — doc Point 34 */}
                {current.media_kind === 'video' && (
                    <button
                        onClick={(e) => { e.stopPropagation(); setMuted((m) => !m); }}
                        data-testid="story-mute-btn"
                        aria-label={muted ? 'Unmute' : 'Mute'}
                        className="absolute top-[max(1.1rem,calc(env(safe-area-inset-top)+0.4rem))] right-12 z-30 w-8 h-8 grid place-items-center rounded-full bg-black/40 text-white hover:bg-black/70">
                        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </button>
                )}

                {/* Media */}
                <div
                    className="absolute inset-0"
                    onMouseDown={() => setPaused(true)}
                    onMouseUp={() => setPaused(false)}
                    onTouchStart={() => setPaused(true)}
                    onTouchEnd={() => setPaused(false)}>
                    {current.media_kind === 'image' ? (
                        <img src={mediaUrl(current)} alt={current.caption || ''}
                            className="absolute inset-0 w-full h-full object-cover" />
                    ) : current.media_kind === 'embed' ? (
                        <iframe src={mediaUrl(current)} title={current.caption || 'Story'}
                            className="absolute inset-0 w-full h-full" allow="autoplay; encrypted-media" allowFullScreen />
                    ) : (
                        <video key={current.id} ref={videoRef} src={mediaUrl(current)} autoPlay playsInline muted={muted}
                            onTimeUpdate={(e) => { const v = e.currentTarget; if (v.duration) setProgress(Math.min(100, (v.currentTime / v.duration) * 100)); }}
                            onEnded={() => advance(1)}
                            className="absolute inset-0 w-full h-full object-cover" />
                    )}
                </div>

                {/* Tap zones — kept clear of the top progress bar and bottom CTA so both stay tappable */}
                <button aria-label="Previous" onClick={() => advance(-1)} className="absolute left-0 top-16 bottom-32 w-1/3 z-10" />
                <button aria-label="Next" onClick={() => advance(1)} className="absolute right-0 top-16 bottom-32 w-1/3 z-10" />

                {/* Caption + CTA (kept clear of the home indicator via safe-area inset) */}
                <div className="absolute bottom-0 left-0 right-0 z-20 px-4 pt-4 pb-[max(1.1rem,calc(env(safe-area-inset-bottom)+0.5rem))] bg-gradient-to-t from-black/90 via-black/40 to-transparent text-white pointer-events-none">
                    {current.caption && (
                        <div className="text-sm sm:text-base leading-snug pointer-events-auto">{current.caption}</div>
                    )}
                    {current.cta_label && (
                        <a href={current.cta_url || '#'} target="_blank" rel="noreferrer"
                            className="pointer-events-auto inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-full bg-[#00DB79] text-slate-900 text-xs font-extrabold uppercase tracking-wider hover:bg-emerald-400"
                            data-testid="story-cta-btn">
                            {current.cta_label} <ExternalLink className="w-3 h-3" />
                        </a>
                    )}
                </div>

                {/* Side hints */}
                {idx > 0 && <div className="absolute left-1 top-1/2 -translate-y-1/2 text-white/40 z-0 pointer-events-none"><ChevronLeft className="w-6 h-6" /></div>}
                {idx < stories.length - 1 && <div className="absolute right-1 top-1/2 -translate-y-1/2 text-white/40 z-0 pointer-events-none"><ChevronRight className="w-6 h-6" /></div>}
            </div>
        </motion.div>
    ), document.body);
}
